import React, { useState, useEffect, useRef } from "react";

import {
  Box,
  Button,
  Chip,
  Collapse,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import CircularProgress from "@mui/material/CircularProgress";

import type { PodStatusSnapshot } from "../../hooks/usePodMonitor";
import { useServiceLogs } from "../../hooks/useServiceLogs";

import { PodRow } from "./PodRow";

function serviceStatusColor(
  pods: PodStatusSnapshot[],
): "success" | "warning" | "error" | "default" {
  if (pods.length === 0) return "default";
  if (pods.every((p) => p.phase === "Running" && p.ready)) return "success";
  if (pods.some((p) => p.phase === "Failed")) return "error";
  return "warning";
}

interface ServiceGroupProps {
  serviceName: string;
  pods: PodStatusSnapshot[];
  alias: string;
  appEnv: string;
}

export function ServiceGroup({
  serviceName,
  pods,
  alias,
  appEnv,
}: ServiceGroupProps) {
  const color = serviceStatusColor(pods);
  const readyCount = pods.filter(
    (p) => p.phase === "Running" && p.ready,
  ).length;

  // Log viewer state
  const [showLogs, setShowLogs] = useState(false);
  const hasFailedPods = pods.some((p) => p.phase === "Failed");
  const hasHighRestarts = pods.some((p) => p.restarts > 2);

  return (
    <Box sx={{ mb: 3 }}>
      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1.5 }}>
        <Typography variant="subtitle2" fontWeight={600} fontFamily="monospace">
          {serviceName}
        </Typography>
        <Chip
          label={`${readyCount}/${pods.length} ready`}
          color={color}
          size="small"
          variant={color === "success" ? "filled" : "outlined"}
        />
        {(hasFailedPods || hasHighRestarts) && (
          <Button
            size="small"
            variant="outlined"
            onClick={() => setShowLogs((s) => !s)}
            sx={{ ml: 1 }}
          >
            {showLogs ? "Hide Logs" : "View Logs"}
          </Button>
        )}
      </Stack>

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell
                sx={{ fontWeight: 600, color: "text.secondary", fontSize: 11 }}
              >
                POD
              </TableCell>
              <TableCell
                sx={{ fontWeight: 600, color: "text.secondary", fontSize: 11 }}
              >
                STATUS
              </TableCell>
              <TableCell
                sx={{ fontWeight: 600, color: "text.secondary", fontSize: 11 }}
              >
                RESTARTS
              </TableCell>
              <TableCell
                sx={{ fontWeight: 600, color: "text.secondary", fontSize: 11 }}
              >
                AGE
              </TableCell>
              <TableCell
                sx={{ fontWeight: 600, color: "text.secondary", fontSize: 11 }}
              >
                CPU
              </TableCell>
              <TableCell
                sx={{ fontWeight: 600, color: "text.secondary", fontSize: 11 }}
              >
                MEMORY
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {pods.map((pod) => (
              <PodRow key={pod.podName} pod={pod} />
            ))}
            {pods.length === 0 && (
              <TableRow>
                <TableCell
                  colSpan={6}
                  align="center"
                  sx={{ py: 3, color: "text.disabled" }}
                >
                  No pods found
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Log viewer - collapses by default */}
      <Collapse in={showLogs} timeout="auto" sx={{ mt: 2 }}>
        <ServiceLogViewer
          serviceName={serviceName}
          alias={alias}
          appEnv={appEnv}
        />
      </Collapse>
    </Box>
  );
}

interface ServiceLogViewerProps {
  serviceName: string;
  alias: string;
  appEnv: string;
}

function ServiceLogViewer({
  serviceName,
  alias,
  appEnv,
}: ServiceLogViewerProps) {
  const { entries, loading, error, refetch } = useServiceLogs(
    alias,
    serviceName,
    appEnv,
    { autoFetch: true, pollInterval: 15000 },
  );
  const bottomRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [entries]);

  return (
    <Paper
      variant="outlined"
      sx={{
        backgroundColor: "#1e1e1e",
        border: "1px solid #333",
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          backgroundColor: "#333",
          px: 2,
          py: 1,
          borderBottom: "1px solid #444",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Typography variant="body2" color="common.white" fontWeight={600}>
          K8s Pod Logs — {serviceName}
        </Typography>
        <Button
          size="small"
          variant="text"
          onClick={() => void refetch()}
          disabled={loading}
          sx={{ color: "grey.400", fontSize: 11, minWidth: 0, px: 1 }}
        >
          {loading ? "Refreshing…" : "Refresh"}
        </Button>
      </Box>
      <Box
        sx={{
          p: 2,
          fontFamily: "monospace",
          fontSize: 12,
          color: "#d4d4d4",
          maxHeight: 400,
          overflowY: "auto",
          backgroundColor: "#1e1e1e",
        }}
      >
        {loading && entries.length === 0 && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress size={24} sx={{ color: "grey.500" }} />
          </Box>
        )}
        {error && (
          <Typography variant="body2" sx={{ color: "error.light" }}>
            {error}
          </Typography>
        )}
        {!loading && !error && entries.length === 0 && (
          <Typography variant="body2" sx={{ color: "grey.500" }}>
            No logs available for this service.
          </Typography>
        )}
        {entries.map((entry) => (
          <Box key={entry.podName} sx={{ mb: 2 }}>
            <Box
              sx={{
                color: "#569cd6",
                fontWeight: 600,
                borderBottom: "1px solid #444",
                pb: 0.5,
                mb: 1,
              }}
            >
              ▶ {entry.podName} / {entry.containerName}
            </Box>
            <Box
              sx={{
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
                color: "#d4d4d4",
              }}
            >
              {entry.logs || (
                <Box component="span" sx={{ color: "grey.600" }}>
                  (no logs)
                </Box>
              )}
            </Box>
          </Box>
        ))}
        <div ref={bottomRef} />
      </Box>
    </Paper>
  );
}
